import React, { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Heart, Loader2 } from "lucide-react";
import workvivoNotificationsService from "../../services/workvivoNotificationsService";
import useCustomToast from "../../hooks/useCustomToast";
import { formatError } from "../../utils/errorHandler";

const WorkvivoInboundKudosToggle = ({ orgId }) => {
  const toast = useCustomToast();
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["workvivo-inbound-kudos", "settings", orgId || "self"],
    queryFn: () => workvivoNotificationsService.getInboundKudos(orgId),
    retry: false,
  });

  // Seed from the server once per org; background refetches must not
  // flip the toggle mid-interaction.
  const seededOrgRef = useRef(null);
  useEffect(() => {
    const key = orgId || "self";
    if (data && seededOrgRef.current !== key) {
      setEnabled(!!data.enabled);
      seededOrgRef.current = key;
    }
  }, [data, orgId]);

  const mutation = useMutation({
    mutationFn: (next) =>
      workvivoNotificationsService.updateInboundKudos(next, orgId),
    onSuccess: (updated) => {
      setEnabled(!!updated.enabled);
      queryClient.invalidateQueries({
        queryKey: ["workvivo-inbound-kudos", "settings"],
      });
      toast.success("Auto-post shoutouts setting saved");
    },
    onError: (err) => {
      toast.error(formatError(err));
      if (data) setEnabled(!!data.enabled); // revert
    },
  });

  const handleToggle = () => {
    const next = !enabled;
    setEnabled(next);
    mutation.mutate(next);
  };

  const webhookUrl = `${window.location.origin}/webhooks/workvivo`;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-500">
        <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading shoutout settings...
      </div>
    );
  }

  // GET returns 404 when no Workvivo integration is configured for the org.
  if (error) {
    const message = formatError(error);
    const notConfigured = /not configured/i.test(message);
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-2">
          <Heart className="w-5 h-5 text-pink-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            Auto-post shoutouts from Workvivo
          </h3>
        </div>
        <p
          className={`text-sm ${
            notConfigured ? "text-gray-500" : "text-red-700"
          }`}
        >
          {notConfigured
            ? "Workvivo integration not configured for this organization."
            : message}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Heart className="w-5 h-5 text-pink-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Auto-post shoutouts from Workvivo
            </h3>
          </div>
          <p className="text-sm text-gray-600">
            When on, a shoutout given in Workvivo drafts a ready-to-post
            recognition for the sender, who reviews it and clicks Post.
          </p>
        </div>
        <button
          type="button"
          onClick={handleToggle}
          disabled={mutation.isPending}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
            enabled ? "bg-indigo-600" : "bg-gray-300"
          }`}
          aria-pressed={enabled}
          aria-label="Toggle auto-post shoutouts from Workvivo"
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
              enabled ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>
      <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
        <p className="font-medium mb-1">One-time Workvivo setup</p>
        <p>
          Shoutouts only arrive after you register this webhook URL in your
          Workvivo app dashboard, so Workvivo can tell us when a shoutout is
          posted:
        </p>
        <code className="mt-2 block break-all rounded bg-blue-100 px-2 py-1 text-xs">
          {webhookUrl}
        </code>
        <p className="mt-2">
          After registering it, turn this setting on and make sure the shoutout
          sender is linked to a user here.
        </p>
      </div>
    </div>
  );
};

export default WorkvivoInboundKudosToggle;
